import {
  BaseEventService,
  type ServiceActivationContext,
} from '@realitycollective/service-framework';
import { createLogger } from '../../util/logger';
import { RoomIdentities, RoomTopics } from '../../protocol/roomTopics';
import {
  StrokeBounds,
  clearAllStrokesJson,
  parseStrokeMessage,
  tryExtractPts,
  type StrokeMessage,
} from '../../protocol/strokeMessages';
import type { ILiveKitRoomService, RoomDataMessage } from './ILiveKitRoomService';
import type {
  AssistStrokeEventMap,
  IAssistStrokeService,
  StrokeRenderCounters,
} from './IAssistStrokeService';

const log = createLogger('Strokes');

export interface AssistStrokeConfig {
  /** Mirror of the detection path's invert flags. Defaults: invertY true, invertX false. */
  readonly invertY?: boolean;
  readonly invertX?: boolean;
  /** Test seam: the arrival clock (ms). Default `performance.now()`. */
  readonly nowMs?: () => number;
}

/** (pts, arrivalMs) ring capacity for ptsRef resolution. */
const PTS_HISTORY_CAPACITY = 64;

/** ptsRef must match a remembered pts within this (90 kHz ticks ~ 200 ms). */
const PTS_MATCH_TOLERANCE_TICKS = 18_000;

const ZERO_COUNTERS: StrokeRenderCounters = { live: 0, unplaced: 0, stale: 0 };

/**
 * The minimal room-side wiring that hands assistants' strokes to the renderer
 * (Plan-04 §3.2). Port of `AssistStrokeService.cs`:
 *
 * - Validation + defense (RA-D4): assistants only, malformed -> log-and-skip
 *   + counter, defensive per-sender rate cap.
 * - Wire -> viewport conversion: stroke points share the bbox y-down
 *   convention, so the SAME invert flags the detection path applies convert
 *   them.
 * - Frame resolution (RA-D6): a small ring maps recent detections `pts` ->
 *   their local arrival time, so a stroke's `ptsRef` resolves to the arrival
 *   the box renderers use for the same frame. Misses fall back to the
 *   stroke's own arrival and count as late.
 */
export class AssistStrokeService
  extends BaseEventService<AssistStrokeEventMap, AssistStrokeConfig>
  implements IAssistStrokeService
{
  private readonly room: ILiveKitRoomService;
  private readonly ptsHistory: Array<{ pts: number; arrivalMs: number }> = [];
  private readonly senderWindows = new Map<string, number[]>();
  private malformedLogCount = 0;
  private renderCounterSource: (() => StrokeRenderCounters) | null = null;
  private unsub: (() => void) | undefined;

  invertY: boolean;
  invertX: boolean;

  private _receivedCount = 0;
  private _droppedCount = 0;
  private _lateCount = 0;

  constructor(context: ServiceActivationContext<AssistStrokeConfig>, room: ILiveKitRoomService) {
    super(context);
    this.room = room;
    this.invertY = context.config.invertY ?? true;
    this.invertX = context.config.invertX ?? false;
  }

  get receivedCount(): number {
    return this._receivedCount;
  }

  get droppedCount(): number {
    return this._droppedCount;
  }

  get lateCount(): number {
    return this._lateCount;
  }

  get renderCounters(): StrokeRenderCounters {
    return this.renderCounterSource ? this.renderCounterSource() : ZERO_COUNTERS;
  }

  setRenderCounterSource(source: (() => StrokeRenderCounters) | null): void {
    this.renderCounterSource = source;
  }

  override start(): void {
    this.unsub = this.room.on('data', (message) => this.onData(message));
  }

  override destroy(): void {
    this.unsub?.();
    super.destroy();
  }

  publishClearAll(): void {
    const json = clearAllStrokesJson();
    if (!this.room.publishData(RoomTopics.strokes, json)) {
      log.warn('clear-all publish refused (room detached) - clearing locally anyway');
    }

    // Publishes do not loop back - raise the local clear so the render
    // module drops everything the wearer no longer wants to see.
    const message = parseStrokeMessage(json);
    if (message) {
      this._receivedCount += 1;
      this.emit('strokeReceived', {
        message,
        senderIdentity: RoomIdentities.wearer,
        arrivalTimeMs: this.nowMs(),
        poseArrivalMs: null,
        late: false,
      });
    }
  }

  private nowMs(): number {
    return this.serviceConfig.nowMs ? this.serviceConfig.nowMs() : performance.now();
  }

  private onData(data: RoomDataMessage): void {
    if (data.topic === RoomTopics.detections) {
      // Only the vision server's payloads carry frame pts (hub table).
      if (data.senderIdentity === RoomIdentities.vision) this.observeDetections(data.json);
      return;
    }

    if (data.topic !== RoomTopics.strokes) return;

    const now = this.nowMs();
    const sender = data.senderIdentity ?? '';

    // Strokes are assistants -> wearer (hub table). Anything else on the
    // topic is dropped and counted - defense, not trust.
    if (!sender.startsWith(RoomIdentities.assistantPrefix)) {
      this._droppedCount += 1;
      return;
    }

    if (!this.allowSender(sender, now)) {
      this._droppedCount += 1;
      return;
    }

    const message = parseStrokeMessage(data.json);
    if (!message) {
      this._droppedCount += 1;
      if (this.malformedLogCount++ < 3) log.warn(`malformed stroke message from '${sender}' skipped`);
      return;
    }

    this._receivedCount += 1;
    this.convertPointsToViewport(message);
    const { poseArrivalMs, late } = this.resolveCaptureArrival(message, now);
    this.emit('strokeReceived', { message, senderIdentity: sender, arrivalTimeMs: now, poseArrivalMs, late });
  }

  /** Wire y-down -> viewport space via the detection path's flags. */
  private convertPointsToViewport(message: StrokeMessage): void {
    message.points = message.points.map((p) => ({
      x: this.invertX ? 1 - p.x : p.x,
      y: this.invertY ? 1 - p.y : p.y,
    }));
  }

  private observeDetections(json: string): void {
    const pts = tryExtractPts(json);
    if (pts === null) return;
    this.ptsHistory.push({ pts, arrivalMs: this.nowMs() });
    while (this.ptsHistory.length > PTS_HISTORY_CAPACITY) this.ptsHistory.shift();
  }

  /**
   * One frame lookup per stroke (§3.2): only begin (and end, which can
   * self-heal a lost begin) carry `ptsRef` and need a pose at all - appends
   * reuse the stroke's frozen pose downstream.
   */
  private resolveCaptureArrival(
    message: StrokeMessage,
    arrivalMs: number,
  ): { poseArrivalMs: number | null; late: boolean } {
    if (message.phase !== 'begin' && message.phase !== 'end') return { poseArrivalMs: null, late: false };

    if (message.ptsRef !== null) {
      let bestDelta = Number.POSITIVE_INFINITY;
      let bestArrival = 0;
      for (const { pts, arrivalMs: arrival } of this.ptsHistory) {
        const delta = Math.abs(pts - message.ptsRef);
        if (delta < bestDelta) {
          bestDelta = delta;
          bestArrival = arrival;
        }
      }

      if (bestDelta <= PTS_MATCH_TOLERANCE_TICKS) {
        // The frame's own arrival -> the identical latency-compensated
        // lookup the box renderers run for that same frame.
        return { poseArrivalMs: bestArrival, late: false };
      }

      // ptsRef present but the frame aged out of the ring - late.
      this._lateCount += 1;
      return { poseArrivalMs: arrivalMs, late: true };
    }

    return { poseArrivalMs: arrivalMs, late: false };
  }

  private allowSender(sender: string, now: number): boolean {
    let window = this.senderWindows.get(sender);
    if (!window) {
      window = [];
      this.senderWindows.set(sender, window);
    }

    const cutoff = now - 1000;
    while (window.length > 0 && window[0]! <= cutoff) window.shift();

    if (window.length >= StrokeBounds.maxMessagesPerSenderPerSecond) return false;

    window.push(now);
    this.pruneIdleSenderWindows(sender, cutoff);
    return true;
  }

  /** Identities are unique per join - without pruning, a long session would keep a window per assistant ever seen (RA-D4: the defense itself must stay bounded). */
  private pruneIdleSenderWindows(keep: string, cutoff: number): void {
    if (this.senderWindows.size <= 64) return;
    for (const [name, window] of this.senderWindows) {
      if (name !== keep && (window.length === 0 || window[window.length - 1]! <= cutoff)) {
        this.senderWindows.delete(name);
      }
    }
  }
}
